import type { JobImpressionSource } from "@workspace/analytics/events/job-impression";
import { trackJobImpression } from "@workspace/analytics/events/job-impression";
import { useSession } from "next-auth/react";
import { useCallback } from "react";
import type { ExtendedUser } from "@/auth";

const STORAGE_KEY = "vacwork:job-impressions";

/**
 * Returns a `track(jobId, organizationId)` function that derives auth state +
 * user type from the active session and dedupes within the browser tab via
 * `sessionStorage`. Use this in place of calling `trackJobImpression`
 * directly so the payload shape and dedup behaviour stay consistent across
 * call sites.
 *
 * Dedup key: `${jobId}:${source}`. Cleared when the tab closes — re-opening
 * fires a fresh impression, which matches "session-scoped unique view"
 * semantics rather than the per-mount behaviour of a `useRef`.
 */
export function useTrackJobImpression(source: JobImpressionSource) {
  const { data: session, status } = useSession();

  return useCallback(
    (jobId: string, organizationId: string, storeId?: string) => {
      if (typeof window !== "undefined") {
        try {
          const raw = window.sessionStorage.getItem(STORAGE_KEY);
          const seen = raw ? (JSON.parse(raw) as string[]) : [];
          const key = `${jobId}:${source}`;
          if (seen.includes(key)) {
            return;
          }
          seen.push(key);
          window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
        } catch {
          // If sessionStorage is blocked (Safari private mode, quota), fall
          // through and emit anyway — over-counting is preferable to silently
          // dropping events.
        }
      }

      const userType = (session?.user as ExtendedUser | undefined)?.type;
      const isAuthenticated = status === "authenticated";
      trackJobImpression({
        job_id: jobId,
        organization_id: organizationId,
        store_id: storeId,
        source,
        is_authenticated: isAuthenticated,
        user_type: isAuthenticated && userType ? userType : "anonymous",
      });
    },
    [session?.user, status, source]
  );
}
